import { Flex, FormControl } from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { get, useFormContext } from 'react-hook-form';

import CustomErrorMessage from '@/common/CustomErrorMessage/CustomErrorMessage';
import CustomShowFormValueOnly from '@/common/CustomShowFormValueOnly/CustomShowFormValueOnly';
import { convertStringToClassName } from '@/utils/convertStringToClassName';
import './CustomFormControl.scss';

interface CustomFormControlProps {
  componentName: string;
  name: string;
  children: ReactNode;
  size?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  showFormValueOnly?: boolean;
}

// Hook to encapsulate useFormContext logic
const useFormControl = (
  name: string,
  isDisabled: boolean,
  isReadOnly: boolean
) => {
  const {
    getValues,
    formState: { errors },
  } = useFormContext();

  const fieldHasError = get(errors, name);
  const isErrorState = fieldHasError && !isDisabled && !isReadOnly;
  const errorMessage =
    isErrorState && (fieldHasError?.message || fieldHasError?.types?.message);

  const inputValue = getValues(name);

  return { isErrorState, errorMessage, inputValue };
};

export const CustomFormControl = ({
  componentName,
  name,
  children,
  size = '',
  isDisabled = false,
  isReadOnly = false,
  showFormValueOnly = false,
}: CustomFormControlProps) => {
  const { isErrorState, errorMessage, inputValue } = useFormControl(
    name,
    isDisabled,
    isReadOnly
  );

  const classNameFromComponentName = convertStringToClassName(componentName);
  const classNameFromName = convertStringToClassName(name);

  if (showFormValueOnly)
    return <CustomShowFormValueOnly inputValue={inputValue} />;

  return (
    <FormControl isInvalid={isErrorState} className={`custom-form-control`}>
      <Flex
        className={`${classNameFromComponentName}-container ${classNameFromName} ${size}`}
      >
        {children}
      </Flex>

      {isErrorState && <CustomErrorMessage errorMessage={errorMessage} />}
    </FormControl>
  );
};
